import axios from "api/api/axios"
import { FinanceData } from "./stockType"

const leadFormURL = "/api/contactUs/lead"
const careerFormURL = "/api/jobs/uploadCv"
const stockDataURL = "/api/finance/paz-stock-model"
const stationsUrl = "/api/dapi/station/mapStations"

export type FormPostResponseData = {
  ErrorCode: number
  ErrorMessage: string | null
  Body: boolean | null
}

type Error = {
  response: {
    data: FormPostResponseData
  }
}
type LeadForm = {
  FirstName: string
  LastName: string
  MobilePhone: string
  Email: string
  Company?: string
  Role?: string
  Subject: string
  Description?: string
  ApprovedCommercialEmails?: boolean
  SourceUrl: string
}
export const postLeadForm = async (formData: LeadForm) => {
  try {
    const response = await axios.post(leadFormURL, formData)
    const { data } = response
    return data
  } catch (err: any) {
    const error = err as Error
    throw error.response?.data || err
  }
}

type CareerForm = {
  JobId: number // integer($int32)
  JobName: string
  JobCode: string
  FirstName: string
  LastName: string
  Email: string
  PhoneNumber: string
  Text: string
  Locations: string
  Company: string
  Field: string
  LinkedIn?: string
  CvFile: string // string($binary) - Representing binary data as a string
  CvFileExtension?: string
  TargetCompany: string
  GRecaptchaResponse: string
}

export const postCareerForm = async (formData: CareerForm) => {
  try {
    const formdata = new FormData()
    Object.keys(formData).forEach((key) => {
      const value = formData[key as keyof CareerForm]
      if (value !== undefined) {
        if (key === "CvFile" && typeof value === "string") {
          // Decode base64 string
          const base64String = value
          const byteCharacters = atob(base64String)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const fileExtension = formData.CvFileExtension?.toLowerCase()
          let fileType = "application/octet-stream"
          if (fileExtension === "pdf") {
            fileType = "application/pdf"
          } else if (fileExtension === "rtf") {
            fileType = "application/rtf"
          } else if (fileExtension === "txt") {
            fileType = "text/plain"
          } else if (fileExtension === "doc" || fileExtension === "docx") {
            fileType = "application/msword"
          }
          const blob = new Blob([byteArray], { type: fileType })
          const file = new File(
            [blob],
            `cv_${formData.JobCode}.${fileExtension}`,
            {
              type: fileType,
            },
          )
          formdata.append(key, file)
        } else {
          formdata.append(key, String(value))
        }
      }
    })
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }

    const response = await axios.post(careerFormURL, formdata, config)
    const { data } = response
    if (data.ErrorCode && data.ErrorCode !== 0) {
      throw data
    }

    return data
  } catch (err: any) {
    const error: Error = err
    // eslint-disable-next-line no-console
    console.error(error)
    throw error.response?.data || err
  }
}

export type StockRange =
  | "Max"
  | "YearToDate"
  | "Years_5"
  | "Years_1"
  | "Months_6"
  | "Months_1"
  | "Days_5"
  | "Days_1"

export type GetStockDataResponse = {
  ErrorCode: number
  ErrorMessage: string | null
  Body: FinanceData
}

export const getStockData = (() => {
  const cache: { [key: string]: GetStockDataResponse } = {}

  return async (range?: StockRange) => {
    const cacheKey = range || "default"

    if (cache[cacheKey]) {
      return cache[cacheKey]
    }

    try {
      const response = await axios.get(
        `${stockDataURL}${range ? `?range=${range}` : ""}`,
      )
      const { data } = response
      cache[cacheKey] = data
      return data
    } catch (err: any) {
      const error = err as Error
      throw error.response?.data || err
    }
  }
})()

export type StationMapRequest = {
  CityId?: number /* int32 */
  LocationLat?: number /* float */
  LocationLng?: number /* float */
  MaxResults?: number | null /* int32 */
  MetaData?: number /* int32 */[]
}

export const postMapStations = async (dataReq: StationMapRequest) => {
  try {
    const response = await axios.post(stationsUrl, dataReq, {
      params: { "api-version": "" },
      headers: { "X-Accept-Language": "he" },
    })
    const { data } = response
    return data
  } catch (err: any) {
    const error: Error = err
    throw error.response?.data || err
  }
}
